import type { BorrowingPowerRuleSet } from "./types.js";

// borrowing-power APRA macroprudential settings, effective from 1 Feb 2026.
//
// Date-keyed (NOT FY-keyed): these are APRA macroprudential settings, which
// change on APRA's schedule, not the financial year. Never edit a shipped
// settings file to reflect a later change — author a new settings-YYYY-MM-DD.ts
// and register it, so a historical assessment resolves against the settings in
// force then.
//
// Figures transcribed from the verification ledgers
// (verification/borrowing-power-estimator-figures.md and
// repayment-buffer-stress-test-figures.md). Apply the ledger's CORRECTION on
// the DTI cap: it IS in force from 1 Feb 2026 (the dossier's "not imposed" stamp
// was stale — that was the 23 Jul 2025 state). All values re-validated 2026-06-22.
export const settings2026_02_01: BorrowingPowerRuleSet = {
  engine: "borrowing-power",
  effectiveFrom: "2026-02-01",
  effectiveTo: null,

  // Figure #1 (CITED, primary) — APRA serviceability buffer "remains at 3
  // percentage points" (APRA macroprudential update, 23 Jul 2025; unchanged by
  // the Nov 2025 DTI activation). Banks assess at product rate + ≥3.0pp.
  serviceabilityBuffer: 0.03,

  // Figure #4 (CITED, CORRECTED) — APRA high-DTI "speed limit", IN FORCE from
  // 1 Feb 2026: ADIs must limit lending at DTI ≥ 6 to 20% of all NEW mortgage
  // lending (owner-occupier and investor portfolios measured separately,
  // quarterly; bridging + new-dwelling purchase/construction exempt). This is a
  // PORTFOLIO-LEVEL lender constraint — NOT a hard per-borrower ">6× = refused"
  // cap. We encode it as data the calc SURFACES as rationing, never as a hard
  // gate on the indicative loan. (APRA, "APRA to limit high debt-to-income home
  // loans…", announced Nov 2025, effective 1 Feb 2026.)
  dtiRationingThreshold: 6,
  dtiPortfolioCapShareOfNewLending: 0.2,

  // Figure #3 (AGENT_VERIFIED — methodology only) — HEM living-expense FLOOR.
  // The Melbourne Institute LICENSES the numeric HEM tables; they are NOT freely
  // published. These rows are a DOCUMENTED APPROXIMATION consistent with the
  // verified methodology (HEM = floor net-of-housing; absolute-basics median +
  // 25th-percentile discretionary; scales with household type + dependants).
  // They are INDICATIVE, period-tagged, and exposed user-adjustable on-surface —
  // NOT precise figures. Re-snapshot quarterly. Any capacity figure driven by
  // these ships stamped `hemIsEstimate: true`.
  hemApproximation: [
    { household: "single", dependants: 0, indicativeAnnualFloor: 26_000 },
    { household: "single", dependants: 1, indicativeAnnualFloor: 33_000 },
    { household: "single", dependants: 2, indicativeAnnualFloor: 39_000 },
    { household: "couple", dependants: 0, indicativeAnnualFloor: 39_000 },
    { household: "couple", dependants: 1, indicativeAnnualFloor: 46_000 },
    { household: "couple", dependants: 2, indicativeAnnualFloor: 52_000 },
  ],

  sources: {
    serviceabilityBuffer:
      "https://www.apra.gov.au/news-and-publications/apra-announces-update-on-macroprudential-settings",
    dtiCap:
      "https://www.apra.gov.au/news-and-publications/apra-to-limit-high-debt-to-income-home-loans-to-constrain-riskier-lending",
    hemMethodology:
      "https://melbourneinstitute.unimelb.edu.au/research/family-and-labour-economics/projects/2012/household-expenditure-measure-quarterly-updates",
  },
  sourcesVerifiedAt: "2026-06-22",
};
